import { useState } from "react";

// Checkbox styled like BlueEnvelopeForm: black border, black fill when checked
const CheckboxItem = ({ label, checked, onChange }) => (
  <label className="flex flex-row items-center gap-1.5 cursor-pointer text-sm leading-tight">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="m-0 w-4 h-4 black-check-box rounded-sm cursor-pointer flex-shrink-0 border-2 border-black !bg-transparent accent-black checked:border-black"
    />
    <span className="text-black">{label}</span>
  </label>
);

export default function WefayoBlueCard() {
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", contactName: "", phone: "" });
  const [relations, setRelations] = useState({
    Mother: false, Father: false, Sister: false, Brother: false, Friend: false, "Other:": false,
  });
  const [conditions, setConditions] = useState({
    "Autism Spectrum Disorder": false,
    "ADHD": false,
    "Anxiety": false,
    "PTSD / Sensory Sensitivities": false,
    "Hearing Differences": false,
    "Intellectual / Developmental Disability": false,
    "Speech / Non-Verbal Communication": false,
    "Difficulty Processing Verbal Instructions": false,
    "Medical / Neurological Condition": false,
    "May Need Extra Time to Respond": false,
    "Other": false,
  });
  const [otherText, setOtherText] = useState("");

  const toggle = (setter, key) =>
    setter((prev) => ({ ...prev, [key]: !prev[key] }));

  const handlePrint = () => {
    window.print();
  };

  // Match BlueEnvelopeForm: sky-100 card surface, print-friendly
  const cardBg = "bg-sky-100";
  const panelBase = `${cardBg} p-5 flex flex-col relative`;
  // Double dotted faint borders between sections (vertical and horizontal)
  const dividerRight = { borderRight: "2px dashed rgba(148,163,184,0.65)"};
  const dividerBottom = { borderBottom: "2px dashed rgba(148,163,184,0.65)" };

  const printStyles = `
    .black-check-box:checked {
      border-color: transparent !important;
      background-color: black !important;
    }
    @media print {
      @page { size: letter; margin: 0.25in; }
      body * { visibility: hidden; }
      .wefayo-blue-card-print-area,
      .wefayo-blue-card-print-area * { visibility: visible; }
      .wefayo-blue-card-print-area {
        position: fixed !important;
        left: 0 !important;
        top: 0 !important;
        right: 0 !important;
        width: 100% !important;
        max-width: 100% !important;
        margin: 0 !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .wefayo-blue-card-no-print { display: none !important; visibility: hidden !important; }
      .wefayo-blue-card-print-area input[type="checkbox"] {
        width: 1rem !important; min-width: 1rem !important;
        height: 1rem !important; min-height: 1rem !important;
        border: 2px solid #000 !important;
        border-radius: 2px !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  `;

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 md:p-6 font-sans print:min-h-0 print:h-0" style={{ backgroundColor: "#313131" }}>
      <style>{printStyles}</style>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:wght@400;600;700&family=Source+Sans+3:wght@400;600&display=swap');
        .card-font { font-family: 'Source Sans 3', sans-serif; }
        .heading-fontx { font-family: 'Lora', serif; }
        .wefayo-blue-card-print-area input[type="text"],
        .wefayo-blue-card-print-area input[type="tel"] { background: transparent !important; outline: none; }
        .wefayo-blue-card-print-area input[type="text"]::placeholder,
        .wefayo-blue-card-print-area input[type="tel"]::placeholder { color: #9ca3af; }
      `}</style>

      {/* Top bar – Instructions & Print (same pattern as envelope template) */}
      <div className="wefayo-blue-card-no-print absolute top-2 md:top-5 right-4 md:right-2 md:right-5 flex flex-row md:flex-col gap-3 z-10">
        <button
          type="button"
          onClick={() => setInstructionsOpen(true)}
          className="px-6 py-3 bg-purple-900 text-white border-none rounded-md text-base cursor-pointer shadow-lg hover:bg-purple-800 transition-colors"
        >
          📝 Instructions
        </button>
        <button
          type="button"
          onClick={handlePrint}
          className="px-6 py-3 bg-purple-900 text-white border-none rounded-md text-base cursor-pointer shadow-lg hover:bg-purple-800 transition-colors"
        >
          🖨️ Print
        </button> 
      </div>

      {/* Instructions dialog (same structure as envelope template) */}
      {instructionsOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]  p-4 print:hidden"
          onClick={() => setInstructionsOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="pocket-card-instructions-title"
        >
          <div
            className="bg-white border-2 border-gray-200 rounded-lg p-6 max-w-4xl w-full shadow-xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setInstructionsOpen(false)}
              className="absolute top-0 right-0 p-2 bg-gray-200 text-gray-900 font-semibold rounded-full hover:bg-gray-300 transition-colors"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <iframe
              src="/blueEnvelopePocketCardInstructions.html"
              className="w-full h-[700px] mt-4"
              style={{ borderRadius: "5px" }}
              title="Pocket card instructions"
            />
          </div>
        </div>
      )}

      <div
        className={`wefayo-blue-card-print-area card-font w-full max-w-2xl shadow-2xl overflow-hidden bg-[#e0f2fe] mt-12 md:mt-0`}
        style={{ boxShadow: "0 8px 40px 0 rgba(60,80,120,0.18)", borderx: "2px dashed rgba(148,163,184,0.65)" }}
      >
        {/* TOP ROW: Panel 4 (left), Panel 1 (right) */}
        <div className="grid grid-cols-2" style={dividerBottom}>
          {/* Panel 4: For the Person Carrying This Card */}
          <div className={`${panelBase} min-h-[280px]`} style={dividerRight}>
            <h2 className="heading-font font-bold text-gray-900 text-base mb-3">
              For the Person Carrying This Card
            </h2>
            <ul className="space-y-1.5 mb-4 text-sm text-black">
              {[
                "This is a voluntary personal communication aid",
                "It is not an official government document",
                "It does not change your legal rights or obligations",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="text-gray-600">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="heading-font font-bold text-gray-900 text-sm mb-2">Legal Disclaimer</p>
            <ul className="space-y-1.5 mb-4 text-sm text-black">
              {[
                "This card is provided as a free courtesy from Wefayo® LLC",
                "It is not a legal document",
                "It is only intended to aid with communication",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="text-gray-600">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="heading-font font-bold text-gray-900 text-sm mb-3">More information at Wefayo.com</p>
            <div className="flex items-center gap-3 mt-auto">
              <div className="flex items-center flex-col ">
                <img src="/logo.png" alt="Wefayo" className="h-12 w-12 object-contain flex-shrink-0" />
                <div className="flex items-center gap-1">
                  <b className="logo-font menu-link-logo-cls text-sm md:text-base ">we</b>
                  <b className="logo-font logo-light menu-link-logo-cls text-sm md:text-base ">fayo</b>
                </div>
              </div>
              

              <img src="/qr-code.png" alt="Wefayo" className="h-16 w-16 object-contain flex-shrink-0" />
              
            </div>
            <p className="text-xs text-gray-600 mt-3">© 2026 Wefayo LLC Wefayo.com</p>
            <span className="absolute bottom-2 left-2 text-gray-500 text-sm font-medium">4</span>
          </div>

          {/* Panel 1: Person With Special Needs (front – show to first responders) */}
          <div className={`${panelBase} min-h-[280px]`} xstyle={dividerBottom}>
            <div className="text-center mb-2">
              <h1 className="heading-font font-bold text-gray-900 text-base uppercase tracking-wide">
                PERSON WITH SPECIAL NEEDS
              </h1>
              <p className="text-xs text-gray-600">(Show to First Responders)</p>
            </div>
            <p className="heading-font font-semibold text-gray-900 text-sm text-center mb-3 leading-snug">
              This person may need extra time and simple, clear instructions.
            </p>

            <div className="mb-2">
              <span className="text-sm text-black">My Name</span>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="m-0 block w-full !bg-transparent border-t-0 border-l-0 border-r-0 border-b border-black text-sm text-gray-900 outline-none py-0.5 mt-0.5"
                placeholder=""
              />
            </div>

            <p className="text-sm text-black mt-2">Emergency Contact:</p>
            <div className="mb-1 mt-0.5">
              <span className="text-sm text-black">Name</span>
              <input
                type="text"
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                className="m-0 block w-full !bg-transparent border-t-0 border-l-0 border-r-0 border-b border-black text-sm text-gray-900 outline-none py-0.5 mt-0.5"
              />
            </div>

            <div className="mb-2">
              <span className="text-sm text-black">Relation</span>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                {Object.keys(relations).map((rel) => (
                  <label key={rel} className="flex items-center gap-1.5 cursor-pointer text-sm leading-tight w-[60px]">
                    <input
                      type="checkbox"
                      checked={relations[rel]}
                      onChange={() => toggle(setRelations, rel)}
                      className="m-0 w-4 h-4 black-check-box rounded-sm cursor-pointer flex-shrink-0 border-2 border-black !bg-transparent"
                    />
                    <span className="text-xs text-black">{rel}</span>
                  </label>
                ))}
                 <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="m-0 inline-flex flex-1 !bg-transparent border-t-0 border-l-0 border-r-0 border-b border-black text-sm text-gray-900 outline-none py-0.5 min-w-[120px]"
                placeholder=""
              />
              </div>
            </div>

            <div className="mb-4">
              <span className="text-sm text-black">Phone <span className="text-sm text-black">(&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;)</span></span>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="m-0 inline-flex flex-1 !bg-transparent border-t-0 border-l-0 border-r-0 border-b border-black text-sm text-gray-900 outline-none py-0.5 min-w-[220px]"
                placeholder=""
              />
              
            </div>

            <p className="text-sm font-bold text-black text-center mb-1">See Inside For Details</p>
            <p className="text-xs font-bold text-gray-700 text-center">Wefayo Blue Envelope Program Pocket Card</p>
            <span className="absolute bottom-2 right-2 text-gray-500 text-sm font-medium">1</span>
          </div>
        </div>

        {/* BOTTOM ROW: Panel 3 (left), Panel 2 (right) – content rotated 180° for fold */}
        <div className="grid grid-cols-2">
          {/* Panel 3: For First Responders (upside down) */}
          <div className={`${panelBase} min-h-[280px]`} style={dividerRight}>
            <div className="h-full w-full flex flex-col p-5 relative" style={{ transform: "rotate(180deg)" }}>
              <h2 className="heading-font font-bold text-gray-900 text-base text-center mb-3">
                For First Responders
              </h2>
              <p className="text-sm text-black mb-2">This person:</p>
              <ul className="space-y-1 mb-4">
                {[
                  "May appear anxious or overwhelmed",
                  "Bright lights may increase stress",
                  "Loud noise may increase stress",
                  "Give one instruction at a time",
                  "Use clear, simple language",
                  "Allow extra time for responses",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-black">
                    <span className="mt-1 text-gray-600">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-black text-center mt-auto">
                If needed, contact the emergency person listed on front.
              </p>
              <span className="absolute bottom-[-10px] right-[-10px] text-gray-500 text-sm font-medium">3</span>
            </div>
          </div>

          {/* Panel 2: Information About Me (upside down) */}
          <div className={`${panelBase} min-h-[280px]`}>
            <div className="h-full w-full flex flex-col p-5 relative" style={{ transform: "rotate(180deg)" }}>
              <div className="text-center mb-3">
                <h2 className="heading-font font-bold text-gray-900 text-base uppercase tracking-wide">
                  INFORMATION ABOUT ME
                </h2>
                <p className="text-xs text-gray-600">(Check all that apply)</p>
              </div>

              <div className="space-y-2 flex-1">
                {Object.keys(conditions).filter(c => c !== "Other").map((cond) => (
                  <CheckboxItem
                    key={cond}
                    label={cond}
                    checked={conditions[cond]}
                    onChange={() => toggle(setConditions, cond)}
                  />
                ))}
                <label className="flex flex-row items-center gap-1.5 cursor-pointer text-sm leading-tight">
                  <input
                    type="checkbox"
                    checked={conditions["Other"]}
                    onChange={() => toggle(setConditions, "Other")}
                    className="m-0 w-4 h-4 black-check-box rounded-sm cursor-pointer flex-shrink-0 border-2 border-black !bg-transparent"
                  />
                  <span className="text-black">Other:</span>
                  <input
                    type="text"
                    value={otherText}
                    onChange={(e) => setOtherText(e.target.value)}
                    className="m-0 ml-1 flex-1 !bg-transparent border-t-0 border-l-0 border-r-0 border-b border-black text-sm text-gray-900 outline-none py-0.5 w-32 min-w-0"
                  />
                </label>
              </div>

              <span className="absolute bottom-[-10px] left-[-10px] text-gray-500 text-sm font-medium">2</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
